import { useEffect, useState, Fragment } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PageHeader from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Award, GraduationCap, Download, Sparkles, Eye, FileText } from "lucide-react";
import { format } from "date-fns";
import { buildCertificatePdf, downloadCertificate } from "@/components/common/CertificateDownload";
import { buildLetterPdf, downloadLetter } from "@/components/common/LetterOfGoodStanding";
import { emailMembershipDocs } from "@/lib/membershipDocs";
import { toast } from "@/hooks/use-toast";
import { memberNumberOf } from "@/lib/membershipNumber";

interface CertMeta { name: string; certId: string; date: string; appId: string; city?: string; country?: string; gender?: string | null }
type DocKind = "certificate" | "letter";

// Build the PDF bytes for either document from the same metadata.
const buildDoc = (kind: DocKind, name: string, certId: string, date: string, city?: string, country?: string, gender?: any) =>
  kind === "certificate"
    ? buildCertificatePdf(name, certId, date)
    : buildLetterPdf(name, certId.toUpperCase(), date, city, country, gender);

// Thumbnail card — loads the PDF (certificate or letter) asynchronously when mounted
const DocPreview = ({ meta, kind, onClick }: { meta: CertMeta; kind: DocKind; onClick: () => void }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    setError(false);
    buildDoc(kind, meta.name, meta.certId, meta.date, meta.city, meta.country, meta.gender)
      .then((bytes) => {
        if (revoked) return;
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
        setPdfUrl(URL.createObjectURL(blob));
      })
      .catch((err) => {
        if (!revoked) setError(true);
      });
    return () => {
      revoked = true;
      setPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [kind, meta.name, meta.certId, meta.date, meta.city, meta.country, meta.gender]);

  return (
    <div
      className="relative cursor-pointer group rounded-lg overflow-hidden border border-border/50 bg-white shadow-sm hover:shadow-md transition-all duration-300"
      onClick={onClick}
    >
      <div className="w-full h-[180px] overflow-hidden flex items-center justify-center bg-muted/30">
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full h-full pointer-events-none"
            tabIndex={-1}
            title={kind === "certificate" ? "Certificate preview" : "Letter of Good Standing preview"}
          />
        ) : error ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Award className="h-8 w-8 text-primary/30" />
            <span className="text-xs">Preview unavailable</span>
          </div>
        ) : (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        )}
      </div>
      <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-300 flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary text-primary-foreground rounded-full p-2.5 shadow-lg">
          <Eye className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const Certificates = () => {
  const { user } = useAuth();
  const [certifiedApps, setCertifiedApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<{ meta: CertMeta; kind: DocKind } | null>(null);
  const [dialogPdfUrl, setDialogPdfUrl] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("applications")
      .select("*")
      .eq("user_id", user.id)
      .in("status", ["paid", "certified"])
      .then(({ data }) => {
        setCertifiedApps(data || []);
        setLoading(false);
      });
  }, [user]);

  // Build PDF blob URL when dialog opens
  useEffect(() => {
    if (!selectedDoc) { setDialogPdfUrl(null); setDialogError(false); return; }
    let revoked = false;
    setDialogError(false);
    buildDoc(selectedDoc.kind, selectedDoc.meta.name, selectedDoc.meta.certId, selectedDoc.meta.date, selectedDoc.meta.city, selectedDoc.meta.country, selectedDoc.meta.gender)
      .then((bytes) => {
        if (revoked) return;
        const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
        setDialogPdfUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!revoked) setDialogError(true);
      });
    return () => {
      revoked = true;
      setDialogPdfUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [selectedDoc]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader title="My Certificates" crumbLabel="Dashboard" crumbTo="/student" />

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : certifiedApps.length === 0 ? (
          <Card className="glass-premium animate-fade-up">
            <CardContent className="flex flex-col items-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <GraduationCap className="h-8 w-8 text-primary/50" />
              </div>
              <h3 className="text-lg font-medium text-foreground">No certificates yet</h3>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                Certificates will appear here once your application is certified.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {certifiedApps.map((app, i) => {
              const certId = memberNumberOf(app);
              const dateStr = format(new Date(app.updated_at), "MMMM d, yyyy");
              const meta: CertMeta = { name: app.full_name, certId, date: dateStr, appId: app.id, city: app.city, country: app.country, gender: app.gender };

              return (
                <Fragment key={app.id}>
                  {/* Certificate */}
                  <Card
                    className="glass-premium card-hover animate-fade-up overflow-hidden"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <CardContent className="p-0">
                      <div className="gradient-warm h-1.5" />
                      <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="certified-badge flex items-center gap-1.5 text-xs">
                            <Sparkles className="h-3 w-3 animate-sparkle" />
                            Certificate
                          </span>
                        </div>

                        <DocPreview meta={meta} kind="certificate" onClick={() => setSelectedDoc({ meta, kind: "certificate" })} />

                        <p className="text-sm font-semibold text-foreground">{app.full_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Membership No: {certId} · {format(new Date(app.updated_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Letter of Good Standing */}
                  <Card
                    className="glass-premium card-hover animate-fade-up overflow-hidden"
                    style={{ animationDelay: `${i * 0.1 + 0.05}s` }}
                  >
                    <CardContent className="p-0">
                      <div className="gradient-hero h-1.5" />
                      <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-primary bg-primary/10 rounded-full px-2.5 py-1">
                            <FileText className="h-3 w-3" />
                            Letter of Good Standing
                          </span>
                        </div>

                        <DocPreview meta={meta} kind="letter" onClick={() => setSelectedDoc({ meta, kind: "letter" })} />

                        <p className="text-sm font-semibold text-foreground">{app.full_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Ref: {certId.toUpperCase()} · {format(new Date(app.updated_at), "MMM d, yyyy")}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Fragment>
              );
            })}
          </div>
        )}

        {/* Full-size dialog (certificate or letter) */}
        <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
          <DialogContent className="max-w-[95vw] sm:max-w-4xl h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedDoc?.kind === "letter" ? <FileText className="h-5 w-5 text-primary" /> : <Award className="h-5 w-5 text-primary" />}
                {selectedDoc?.kind === "letter" ? "Letter of Good Standing" : "Certificate"} — {selectedDoc?.meta.name}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 rounded-lg overflow-hidden border bg-white flex items-center justify-center">
              {dialogPdfUrl ? (
                <iframe src={dialogPdfUrl} className="w-full h-full" title="Document full view" />
              ) : dialogError ? (
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <Award className="h-12 w-12 text-primary/30" />
                  <p className="text-sm">Failed to generate preview.</p>
                  <p className="text-xs">You can still download it below.</p>
                </div>
              ) : (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              {selectedDoc?.kind === "letter" ? (
                <Button
                  className="gradient-warm text-primary-foreground"
                  onClick={() => {
                    if (selectedDoc) downloadLetter(selectedDoc.meta.name, selectedDoc.meta.certId.toUpperCase(), selectedDoc.meta.date, selectedDoc.meta.city, selectedDoc.meta.country, selectedDoc.meta.gender as any);
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Letter of Good Standing
                </Button>
              ) : (
                <Button
                  className="gradient-warm text-primary-foreground"
                  onClick={async () => {
                    if (!selectedDoc) return;
                    const { meta } = selectedDoc;
                    await downloadCertificate(meta.name, meta.certId, meta.date);
                    try {
                      await emailMembershipDocs(meta.appId, { fullName: meta.name, certId: meta.certId, date: meta.date });
                      toast({ title: "Documents emailed", description: "Certificate & Letter of Good Standing sent to your email." });
                    } catch { /* best-effort */ }
                  }}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Certificate
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Certificates;
